import { useMemo, useState } from "react";
import { useDisplacementMap } from "./DisplacementMapContext";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

export function DisplacementMapViewer() {
    const {
        svg,
        viewBoxAttr,
        displacedSvgContent,
        canExport,
        handleDownload,
    } = useDisplacementMap();

    const [showOriginal, setShowOriginal] = useState(false);

    const displayContent = useMemo(() => {
        const raw = showOriginal ? svg?.content : displacedSvgContent;
        if (!raw) return null;
        // Strip outer <svg> tags so we can embed inside our own controlled svg
        return raw.replace(/<\/?svg[^>]*>/gi, "").trim();
    }, [showOriginal, svg, displacedSvgContent]);

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    return (
        <div className="panel-viewer displacement-map-viewer">
            <div className="path-tool-preview">
                {/* Zoom controls */}
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                    {displacedSvgContent && (
                        <button
                            type="button"
                            className="zoom-button"
                            onClick={() => setShowOriginal((v) => !v)}
                            title="Toggle original / displaced"
                            style={{ marginLeft: "12px", minWidth: "90px" }}
                        >
                            {showOriginal ? "Original" : "Displaced"}
                        </button>
                    )}
                </div>

                {/* Scrollable SVG */}
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        {displayContent ? (
                            <svg
                                ref={svgRef}
                                viewBox={viewBox}
                                xmlns="http://www.w3.org/2000/svg"
                                className="path-tool-svg"
                                style={{ touchAction: "none" }}
                                {...panHandlers}
                            >
                                <g
                                    dangerouslySetInnerHTML={{
                                        __html: displayContent,
                                    }}
                                />
                            </svg>
                        ) : (
                            <div className="svg-placeholder">
                                <p>Import an SVG file to get started</p>
                                <p className="svg-placeholder-hint">
                                    The displacement field will warp all shape
                                    coordinates continuously
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {svg
                            ? `${svg.name} — viewBox: ${viewBoxAttr}`
                            : "Waiting for SVG file."}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
